import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Plus, AlertTriangle, FileSearch, ListChecks } from "lucide-react";
import { initials, timeAgo } from "@/lib/ui";
import { RCA_SEVERITY, RCA_STATUS } from "@/lib/rca";
import { SeverityBadge, RcaStatusBadge } from "@/components/rca/RcaBits";
import CreateRcaDialog from "@/components/rca/CreateRcaDialog";
import RcaTrends from "@/components/rca/RcaTrends";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function Filter({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-44 bg-white" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{placeholder}</SelectItem>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Stat({ label, value, tone, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 min-w-[140px]" data-testid={testid}>
      <p className={`text-2xl font-display font-bold ${tone}`}>{value}</p>
      <p className="text-[11px] text-slate-500">{label}</p>
    </div>
  );
}

function RcaRow({ r }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(`/rca/${r.id}`)} className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-sm transition-[border-color,box-shadow] flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`rca-row-${r.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">{r.key}</span>
          <SeverityBadge sev={r.severity} /><RcaStatusBadge status={r.status} />
          {r.actions_overdue > 0 && <span className="text-[11px] font-semibold text-red-600 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{r.actions_overdue} overdue</span>}
        </div>
        <p className="text-sm font-semibold text-slate-900 truncate">{r.title}</p>
        <p className="text-xs text-slate-500 mt-0.5">{r.client_name} · updated {timeAgo(r.updated_at)}</p>
      </div>
      <div className="flex items-center gap-4 shrink-0">
        <span className="text-xs text-slate-500 inline-flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" />{r.actions_done}/{r.actions_total}</span>
        <span className="flex items-center gap-2">
          <Avatar className="w-7 h-7"><AvatarImage src={r.engineer_avatar} /><AvatarFallback className="bg-indigo-100 text-indigo-700 text-[10px]">{initials(r.engineer_name)}</AvatarFallback></Avatar>
          <span className="text-xs text-slate-600">{r.engineer_name}</span>
        </span>
      </div>
    </button>
  );
}

export default function RcaListPage() {
  const [rcas, setRcas] = useState([]);
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ client_id: "all", engineer_id: "all", severity: "all", status: "all" });
  const [creating, setCreating] = useState(false);

  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "all"));
    api.get("/rcas", { params }).then((r) => setRcas(r.data));
  }, [f]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const open = rcas.filter((r) => r.status !== "closed");

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px]" data-testid="rca-list-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Root Cause Analysis</h1>
          <p className="text-sm text-slate-500 mt-1">Blameless post-incident reviews, mapped to client and responsible engineer.</p>
        </div>
        <Button onClick={() => setCreating(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-rca-button"><Plus className="w-4 h-4 mr-1.5" />New RCA</Button>
      </div>
      <Tabs defaultValue="list">
      <TabsList className="mb-5">
        <TabsTrigger value="list" data-testid="rca-tab-list">All RCAs</TabsTrigger>
        <TabsTrigger value="trends" data-testid="rca-tab-trends">Trends</TabsTrigger>
      </TabsList>
      <TabsContent value="trends"><RcaTrends /></TabsContent>
      <TabsContent value="list">
      <div className="flex gap-3 mb-5 overflow-x-auto scroll-thin pb-1">
        <Stat label="Open RCAs" value={open.length} tone="text-slate-900" testid="rca-stat-open" />
        <Stat label="Open SEV1 / SEV2" value={open.filter((r) => ["SEV1", "SEV2"].includes(r.severity)).length} tone="text-red-600" testid="rca-stat-high" />
        <Stat label="Awaiting review" value={rcas.filter((r) => r.status === "in_review").length} tone="text-sky-600" testid="rca-stat-review" />
        <Stat label="Overdue actions" value={rcas.reduce((s, r) => s + r.actions_overdue, 0)} tone="text-amber-600" testid="rca-stat-overdue" />
      </div>
      <div className="flex gap-2 mb-5 overflow-x-auto scroll-thin pb-1 [&>*]:shrink-0" data-testid="rca-filters">
        <Filter value={f.client_id} onChange={set("client_id")} placeholder="All clients" testid="rca-filter-client" options={users.filter((u) => u.role === "client").map((u) => [u.id, u.company || u.name])} />
        <Filter value={f.engineer_id} onChange={set("engineer_id")} placeholder="All engineers" testid="rca-filter-engineer" options={users.filter((u) => u.role !== "client").map((u) => [u.id, u.name])} />
        <Filter value={f.severity} onChange={set("severity")} placeholder="All severities" testid="rca-filter-severity" options={Object.entries(RCA_SEVERITY).map(([k, v]) => [k, v.label])} />
        <Filter value={f.status} onChange={set("status")} placeholder="All statuses" testid="rca-filter-status" options={Object.entries(RCA_STATUS).map(([k, v]) => [k, v.label])} />
      </div>
      <div className="space-y-2.5">
        {rcas.map((r) => <RcaRow key={r.id} r={r} />)}
        {!rcas.length && (
          <div className="text-center py-16 text-slate-400" data-testid="rca-empty">
            <FileSearch className="w-8 h-8 mx-auto mb-2" />No RCAs yet. Create one here or from a ticket.
          </div>
        )}
      </div>
      </TabsContent>
      </Tabs>
      {creating && <CreateRcaDialog onClose={() => setCreating(false)} />}
    </div>
  );
}
